import { defineStore } from "pinia";
import { computed, reactive, readonly, Ref, ref } from "vue";

import { LogService } from "@/services";
import {
  ProfileView,
  Search,
  SearchItem,
  SearchOrder,
  SearchQuery,
  SearchQueryUpdatePayload,
  SearchResultsPagination,
} from "@/types";

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export const useSearchStore = defineStore("search", () => {
  const currentSearch: Ref<Search> = ref({
    currentSearchQuery: {
      // Search configuration
      page: 1,
      pageSize: 12,
      order: SearchOrder.DEFAULT,
      view: ProfileView.DEFAULT,
      hidePayWalledProfiles: undefined,

      // Local variables
      autoCompleteKeyword: "",

      // Vignette simple search
      keyword: "",

      // Vignette advanced search
      currentOfficeCongressionalDistrict: { id: "", name: "" },
      currentOfficeStates: [],
      politicalParty: [],
      religion: { id: "", name: "" }, // also in congressional/federal search
      gender: "", // also in congressional/federal search
      ethnicity: { id: "", name: "" }, // also in congressional/federal search
      state: { id: "", name: "" }, // also in congressional/federal search
      country: { id: "", name: "" },
      editorialGroup: { id: "", name: "" },
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      committee: [],
      caucuses: [],
      agencies: [],
      /**
       * This is the string that is sent to the backend for search purposes.
       * It gets built from the AgencyTreeSelect component.
       */
      agencyNotation: "",
      city: "",
      education: "",
      includeFormer: false,
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups - NestedLookupTableItem but nullable
      sig: null,
      sigOperator: { id: "gte", name: "Greater Than" },
      sigRating: { id: "50", name: "50" },

      // congressional/federal search
      appointment: [],
      appointment2: "",
      caucus: [],
      degree: "",
      district: [],
      interest: [],
      interest2: [],
      jobtitle: [],
      jobtitle2: [],
      jobtitle2_option: "",
      majmin: "",
      office_location: [],
      offset: "",
      party: "",
      school: "",
      topagency: "",
      topagency2: "",
      type: "",

      // congressional search
      office: [],
      // federal search
      agency: [],
    },
    cachedResults: [],
    keepCachedResults: false,
    autocomplete: {
      isLoading: false,
      results: [],
    },
    isLoading: false,
    keepKeyword: false,
  });

  const lastSearchString = ref("");
  const lastSearch: Ref<{
    query: SearchQuery;
    pagination: SearchResultsPagination;
  }> = ref({
    query: {
      // Search configuration
      page: 1,
      pageSize: 12,
      order: SearchOrder.DEFAULT,
      view: ProfileView.DEFAULT,
      hidePayWalledProfiles: undefined,

      // Local variables
      autoCompleteKeyword: "",

      // Vignette simple search
      keyword: "",

      // Vignette search
      currentOfficeCongressionalDistrict: { id: "", name: "" },
      currentOfficeStates: [],
      politicalParty: [],
      religion: { id: "", name: "" },
      gender: "",
      ethnicity: { id: "", name: "" },
      state: { id: "", name: "" },
      country: { id: "", name: "" },
      editorialGroup: { id: "", name: "" },
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      committee: [],
      caucuses: [],
      agencies: [],
      /**
       * This is the string that is sent to the backend for search purposes.
       * It gets built from the AgencyTreeSelect component.
       */
      agencyNotation: "",
      city: "",
      education: "",
      includeFormer: false,
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups - NestedLookupTableItem but nullable
      sig: null,
      sigOperator: { id: "gte", name: "Greater Than" },
      sigRating: { id: "50", name: "50" },
      // congressional/federal search
      appointment: [],
      appointment2: "",
      caucus: [],
      degree: "",
      district: [],
      interest: [],
      interest2: [],
      jobtitle: [],
      jobtitle2: [],
      jobtitle2_option: "",
      majmin: "",
      office_location: [],
      offset: "",
      party: "",
      school: "",
      topagency: "",
      topagency2: "",
      type: "",

      // congressional search
      office: [],
      // federal search
      agency: [],
    },
    pagination: {
      pageSize: 12,
      page: 1,
      totalPages: 1,
      totalHits: 0,
      order: SearchOrder.DEFAULT,
    },
  });

  const searchFormIsHidden = ref(false);
  const agencyTreeSelectKey = ref(0);

  function _getDefaultSearch(): Search {
    return {
      currentSearchQuery: {
        // Search configuration
        page: 1,
        pageSize: 12,
        order: SearchOrder.DEFAULT,
        view: ProfileView.DEFAULT,
        hidePayWalledProfiles: undefined,

        // Local variables
        autoCompleteKeyword: "",

        // Vignette simple search
        keyword: "",

        // Vignette advanced search
        currentOfficeCongressionalDistrict: { id: "", name: "" },
        currentOfficeStates: [],
        politicalParty: [],
        religion: { id: "", name: "" },
        gender: "",
        ethnicity: { id: "", name: "" },
        state: { id: "", name: "" },
        country: { id: "", name: "" },
        editorialGroup: { id: "", name: "" },
        profileTypes: [],
        keyPolicyAreas: [],
        industries: [],
        committees: [],
        committee: [],
        caucuses: [],
        agencies: [],
        /**
         * This is the string that is sent to the backend for search purposes.
         * It gets built from the AgencyTreeSelect component.
         */
        agencyNotation: "",
        city: "",
        education: "",
        includeFormer: false,
        // Birthdate
        birthYear: { id: "", name: "" },
        age: { id: "", name: "" },
        birthDateOperator: { id: "exact", name: "Born On" },
        // Special Interest Groups - NestedLookupTableItem but nullable
        sig: null,
        sigOperator: { id: "gte", name: "Greater Than" },
        sigRating: { id: "50", name: "50" },
        // congressional/federal search
        appointment: [],
        appointment2: "",
        caucus: [],
        degree: "",
        district: [],
        interest: [],
        interest2: [],
        jobtitle: [],
        jobtitle2: [],
        jobtitle2_option: "",
        majmin: "",
        office_location: [],
        offset: "",
        party: "",
        school: "",
        topagency: "",
        topagency2: "",
        type: "",

        // congressional search
        office: [],
        // federal search
        agency: [],
      },
      cachedResults: [],
      keepCachedResults: false,
      autocomplete: {
        isLoading: false,
        results: [],
      },
      isLoading: false,
      keepKeyword: false,
    };
  }

  function getDefaultSearchQuery(): SearchQuery {
    return reactive({
      // Search configuration
      page: 1,
      pageSize: 12,
      order: SearchOrder.DEFAULT,
      view: ProfileView.DEFAULT,
      hidePayWalledProfiles: undefined,

      // Local variables
      autoCompleteKeyword: "",

      // Vignette simple search
      keyword: "",

      // Vignette advanced search
      currentOfficeCongressionalDistrict: { id: "", name: "" },
      currentOfficeStates: [],
      politicalParty: [],
      religion: { id: "", name: "" },
      gender: "",
      ethnicity: { id: "", name: "" },
      state: { id: "", name: "" },
      country: { id: "", name: "" },
      editorialGroup: { id: "", name: "" },
      profileTypes: [],
      keyPolicyAreas: [],
      industries: [],
      committees: [],
      committee: [],
      caucuses: [],
      agencies: [],
      /**
       * This is the string that is sent to the backend for search purposes.
       * It gets built from the AgencyTreeSelect component.
       */
      agencyNotation: "",
      city: "",
      education: "",
      includeFormer: false,
      // Birthdate
      birthYear: { id: "", name: "" },
      age: { id: "", name: "" },
      birthDateOperator: { id: "exact", name: "Born On" },
      // Special Interest Groups - NestedLookupTableItem but nullable
      sig: null,
      sigOperator: { id: "gte", name: "Greater Than" },
      sigRating: { id: "50", name: "50" },
      // congressional/federal search
      appointment: [],
      appointment2: "",
      caucus: [],
      degree: "",
      district: [],
      interest: [],
      interest2: [],
      jobtitle: [],
      jobtitle2: [],
      jobtitle2_option: "",
      majmin: "",
      office_location: [],
      offset: "",
      party: "",
      school: "",
      topagency: "",
      topagency2: "",
      type: "",

      // congressional search
      office: [],
      // federal search
      agency: [],
    });
  }

  const isAutocompleteSearchLoading = computed<boolean>(
    () => currentSearch.value.autocomplete.isLoading
  );

  const isSearchLoading = computed<boolean>(
    () => currentSearch.value.isLoading
  );

  const autocompleteResults = computed<SearchItem[]>(
    () => currentSearch.value.autocomplete.results
  );

  const getCurrentSearchQuery = computed<SearchQuery>(
    () => currentSearch.value.currentSearchQuery
  );

  const lastSearchQuery = computed<SearchQuery>(
    () => lastSearch.value.query || getDefaultSearchQuery()
  );

  const getLastSearchQueryView = computed<ProfileView>(
    () => lastSearchQuery.value.view || ProfileView.DEFAULT
  );

  const getLastSearchQueryPageNumber = computed<number>(
    () => lastSearch.value.pagination.page
  );

  const getLastSearchQueryOrder = computed<SearchOrder>(
    () => lastSearch.value.pagination.order
  );

  const getLastSearchQueryPageSize = computed<number>(
    () => lastSearch.value.pagination.pageSize
  );

  const getLastSearchQueryPageCount = computed<number>(
    () => lastSearch.value.pagination.totalPages
  );

  const getLastSearchQueryHitsCount = computed<number>(
    () => lastSearch.value.pagination.totalHits
  );

  const getVignetteSearchCurrentPageItems = computed<SearchItem[] | null>(
    () =>
      currentSearch.value.cachedResults[lastSearch.value.pagination.page] ?? []
  );

  function setAutocompleteSearchLoading(loading: boolean): void {
    LogService.debug(`Setting setAutoCompleteSearchLoading to '${loading}'`);
    currentSearch.value.autocomplete.isLoading = loading;
  }

  function setAutocompleteSearchResults(results: SearchItem[]): void {
    LogService.debug(
      `Setting setAutoCompleteSearchResults for results.length: '${results.length}'`
    );
    currentSearch.value.autocomplete.results = results;
  }

  function setCurrentSearchQuery(currentQuery: SearchQuery): void {
    LogService.debug(
      `Setting setSearchCurrentQuery to '${JSON.stringify(currentQuery)}'`
    );
    currentSearch.value.currentSearchQuery = currentQuery;
  }

  function setLastSearchQuery(previousQuery: SearchQuery): void {
    LogService.debug(
      `Setting setLastSearchQuery to '${JSON.stringify(previousQuery)}'`
    );
    lastSearch.value.query = previousQuery;
  }

  function setCurrentSearchQueryField(payload: SearchQueryUpdatePayload): void {
    if (payload.value === null) {
      payload.value =
        getDefaultSearchQuery()[payload.field as keyof SearchQuery];
    }
    // No coercion here; SearchService.generateQuery handles param stringification
    LogService.debug(
      `Setting setSearchCurrentQueryField to '${JSON.stringify(payload)}'`
    );
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    currentSearch.value.currentSearchQuery[payload.field] = payload.value;
  }

  function setLastSearchQueryField(payload: SearchQueryUpdatePayload): void {
    LogService.debug(
      `Setting setSearchLastQueryField to '${JSON.stringify(payload)}'`
    );
    if (lastSearchQuery.value != null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      lastSearch.value.query[payload.field] = payload.value;
    }
  }

  function setSearchIsLoading(loading: boolean): void {
    LogService.debug(`Setting searchLoading to '${loading}'`);
    currentSearch.value.isLoading = loading;
  }

  function setSearchPageNumber(pageNumber: number): void {
    LogService.debug(`Setting setSearchPageNumber to '${pageNumber}'`);
    lastSearch.value.pagination.page = pageNumber;
  }

  function setSearchPageSize(pageSize: number): void {
    LogService.debug(`Setting setSearchPageSize to '${pageSize}'`);
    // Cap this at 100 profiles. Prevents trying to load too many profiles at once.
    lastSearch.value.pagination.pageSize = Math.min(pageSize, 100);
  }

  function setSearchOrder(order: SearchOrder): void {
    LogService.debug(`Setting setSearchOrder to '${order}'`);
    lastSearch.value.pagination.order = order;
  }

  function setSearchKeepCachedResults(keepCachedResults: boolean): void {
    LogService.debug(
      `Setting setSearchKeepCachedResults to '${keepCachedResults}'`
    );
    currentSearch.value.keepCachedResults = keepCachedResults;
  }

  const setSearchResultsPagination = (
    pagination: SearchResultsPagination
  ): void => {
    // Make sure both setSearchResultsPagination and setSearchResults are called at the same time, and are in sync
    LogService.debug(
      `Setting setSearchResultsPagination: '${JSON.stringify(pagination)}'`
    );
    lastSearch.value.pagination = pagination;
  };

  const setSearchResults = (results: SearchItem[]): void => {
    // Make sure both setSearchResultsPagination and setSearchResults are called at the same time, and are in sync
    LogService.debug(
      `Setting setSearchResults for results.length: '${results.length}'`
    );
    currentSearch.value.cachedResults[getLastSearchQueryPageNumber.value] =
      results;
  };

  /**
   * Since there notes to keep these two in sync and to call the above two functions at the same time,
   * this function exists to force that behavior.
   * @param pagination
   * @param results
   */
  function setSearchResultsAndPagination(
    pagination: SearchResultsPagination,
    results: SearchItem[]
  ): void {
    setSearchResultsPagination(pagination);
    setSearchResults(results);
  }

  function resetSearch(): void {
    LogService.debug(`Setting resetSearch to '(empty|null)'`);
    searchFormIsHidden.value = false;
    currentSearch.value = _getDefaultSearch();
    lastSearchString.value = "";
    lastSearch.value.query = getDefaultSearchQuery();
    agencyTreeSelectKey.value += 1;
  }

  return {
    agencyTreeSelectKey: readonly(agencyTreeSelectKey),
    search: currentSearch,
    searchFormIsHidden,
    isAutocompleteSearchLoading,
    isSearchLoading,
    autocompleteResults,
    lastSearchString,
    lastSearchQuery,
    getCurrentSearchQuery,
    getDefaultSearchQuery,
    getLastSearchQueryView,
    getLastSearchQueryPageNumber,
    getLastSearchQueryOrder,
    getLastSearchQueryPageSize,
    getLastSearchQueryPageCount,
    getLastSearchQueryHitsCount,
    getVignetteSearchCurrentPageItems,
    setAutocompleteSearchLoading,
    setAutocompleteSearchResults,
    setCurrentSearchQuery,
    setLastSearchQuery,
    setCurrentSearchQueryField,
    setLastSearchQueryField,
    setSearchIsLoading,
    setSearchPageNumber,
    setSearchPageSize,
    setSearchOrder,
    setSearchKeepCachedResults,
    setSearchResultsAndPagination,
    resetSearch,
  };
});
